import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Download, ExternalLink, Loader2, RefreshCw, Search } from 'lucide-react';
import { archiveApi } from '../mocks/archiveApi';
import {
  ArchiveDoc,
  DOC_KINDS,
  DocKind,
  KIND_STYLE,
  OUTCOMES,
  Outcome,
  YEAR_BUCKETS,
  YearBucket,
  yearBucket,
} from '../types/archive';
import { ArchiveContractSection } from './ArchiveContractSection';
import { ArchiveUnlinkedFolders } from './ArchiveUnlinkedFolders';
import { useAuth } from '../contexts/AuthContext';
import { SidePanel } from '../components/SidePanel';
import { won } from '../utils/format';
import { ErrorBanner, errorText } from '../components/ErrorBanner';

/**
 * 아카이브 — **공고 화면과 같은 모양**이다: 위 필터 한 줄 · 표 · 누르면 오른쪽 패널(2026-10-01).
 * 처음엔 시안(`docs/02-design/canvas/Archive.dc.html`)의 세 칸(필터 · 카드 · 고정 메타데이터)이었는데
 * 공고와 모양이 달라 헷갈렸고, 빈 메타데이터 칸이 늘 자리를 차지했다.
 *
 * **원본은 Drive 에 있다.** 여기서는 찾고, AI 가 뽑은 값을 사람이 고친다.
 *
 * ⚠️ 항목은 **잠정**이다. 설계 문서가 계약서 68건 표본 조사를 선행으로 잡아 뒀다 —
 *    어떤 필드가 일관되게 뽑히는지가 화면 항목을 정한다. 화면에도 적어 둔다.
 */
function period(d: ArchiveDoc): string {
  if (!d.startMonth) return '—';
  const s = d.startMonth.replace('-', '.');
  if (!d.endMonth) return s;
  const e = d.endMonth.slice(0, 4) === d.startMonth.slice(0, 4)
    ? d.endMonth.slice(5)
    : d.endMonth.replace('-', '.');
  return `${s} ~ ${e}`;
}

function months(d: ArchiveDoc): string | null {
  if (!d.startMonth || !d.endMonth) return null;
  const [sy, sm] = d.startMonth.split('-').map(Number);
  const [ey, em] = d.endMonth.split('-').map(Number);
  return `${(ey - sy) * 12 + (em - sm) + 1}개월`;
}

export const ArchivePage: React.FC = () => {
  const [items, setItems] = useState<ArchiveDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  /* 공고처럼 하나씩 고른다. 빈 값이 전체 */
  const [kind, setKind] = useState<DocKind | ''>('');
  const [year, setYear] = useState<YearBucket | ''>('');
  const [outcome, setOutcome] = useState<Outcome | ''>('');
  const [pickedId, setPickedId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  /* 원본(받기 · 근거 쪽)은 회사 계정만 — 앱에는 외부 개발자 계정도 로그인한다. 서버도 같은 기준으로 막는다 */
  const { user } = useAuth();
  const internal = !!user?.email?.toLowerCase().endsWith('@grkcon.com');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const downloadOriginal = async (id: number, fileName: string) => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const url = URL.createObjectURL(await archiveApi.file(id, true));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      setDownloadError(`받지 못했습니다 — ${(e as Error).message}`);
    } finally {
      setDownloading(false);
    }
  };
  const [syncNote, setSyncNote] = useState<string | null>(null);
  /* 동기화가 끝나면 올려서 「연결 안 된 폴더」를 다시 읽게 한다 */
  const [syncCount, setSyncCount] = useState(0);

  /* 조회 실패를 빈 목록으로 보이지 않게 한다(2026-10-23) — 「문서가 없습니다」와 구별되지 않았다 */
  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setItems(await archiveApi.list());
    } catch (e) {
      setLoadError(`문서를 불러오지 못했습니다 — ${errorText(e)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* 30초 안팎 걸린다. 목록에는 추출이 끝난 문서만 보이므로 새 파일이 들어와도 바로 늘지는 않는다 */
  const syncDrive = async () => {
    setSyncing(true);
    setSyncNote(null);
    try {
      const r = await archiveApi.sync();
      setSyncNote(
        `드라이브 ${r.docs.toLocaleString()}개 확인 · 새 파일 ${r.inserted} · 사라짐 ${r.removed}` +
        (r.unlinkedFolders.length ? ` · 연결 안 된 사업 폴더 ${r.unlinkedFolders.length}` : ''),
      );
      setSyncCount((n) => n + 1);
      await load();
    } catch (e) {
      setSyncNote(`동기화 실패 — ${(e as Error).message}`);
    } finally {
      setSyncing(false);
    }
  };

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((d) => {
      if (kind && d.kind !== kind) return false;
      if (year && yearBucket(d.docMonth) !== year) return false;
      if (outcome && d.outcome !== outcome) return false;
      if (!needle) return true;
      return [d.title, d.projectName, d.clientName, d.summary, ...d.keywords]
        .join(' ').toLowerCase().includes(needle);
    });
  }, [items, q, kind, year, outcome]);

  const picked = items.find((d) => d.id === pickedId) ?? null;

  /* AI 추출 진행 — 시안의 `27 / 68` 자리다.
   *
   * 시안은 「계약서 68건」이라고 적었지만 **검수 대상 전체**를 센다.
   * 계약서만 세면 제안서·견적이 검수 대기인데 배너는 다 끝났다고 말한다 —
   * 그러면 바로 옆 「검수하기」가 배너와 다른 곳으로 간다. */
  const extracted = items.filter((d) => d.extracted);
  const done = extracted.filter((d) => d.reviewed).length;
  const left = extracted.length - done;

  const setReviewed = async (id: number, v: boolean) => {
    setSaving(true);
    setDownloadError(null);
    try {
      await archiveApi.markReviewed(id, v);
      await load();
      // 검수 중이면 다음 「검수 필요」 문서로 넘어간다 — 전엔 패널을 닫고 다시 「검수하기」를 눌러야 했다(#111)
      if (v) {
        const next = items.find((d) => d.extracted && !d.reviewed && d.id !== id);
        if (next) setPickedId(next.id);
      }
    } catch (e) {
      setDownloadError(`검수 표시를 저장하지 못했습니다 — ${(e as Error).message}`);
    } finally { setSaving(false); }
  };

  const opt = (label: string, n: number) => `${label} (${n})`;
  const selectCls =
    'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';

  return (
    <div className="p-6 lg:p-8 max-w-7xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">
            아카이브
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            원본은 Google Drive · 여기에는 찾을 수 있게 메타데이터만 둡니다 · 새 파일은 드라이브에 올리면 매일 05:00 에 들어옵니다
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {syncNote && <span className="text-xs text-slate-500">{syncNote}</span>}
          <button
            onClick={syncDrive}
            disabled={syncing}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {syncing ? '동기화 중…' : 'Drive 동기화'}
          </button>
        </div>
      </div>

      {/* 드라이브에 새로 생긴 사업 폴더. 없으면 띄우지 않는다 */}
      <ArchiveUnlinkedFolders refreshKey={syncCount} onChanged={load} />

      {/* AI 추출 진행. 남은 것이 없으면 띄우지 않는다 */}
      {left > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <span className="text-xs font-semibold text-amber-900">AI 추출</span>
            <span className="ml-2 text-xs text-amber-800">
              고객사 · 기간 · 금액 · 키워드를 뽑았습니다 — <b>{left}건</b>이 확인을 기다립니다
            </span>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-sm font-bold text-amber-900 tabular-nums">
              {done} / {extracted.length}
            </span>
            <button
              onClick={() => {
                setKind(''); setYear(''); setOutcome('');
                setQ('');
                const first = items.find((d) => d.extracted && !d.reviewed);
                if (first) setPickedId(first.id);
              }}
              className="px-2.5 py-1 rounded-lg text-xs font-medium border border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
            >
              검수하기
            </button>
          </div>
        </div>
      )}

      {/* 필터 — 공고와 같은 한 줄 */}
      <div className="bg-white rounded-lg border border-slate-200 p-5 mb-6 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-slate-500 mb-1">검색어</label>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="문서명 · 프로젝트명 · 고객사 · 태그"
              className="w-full pl-9 pr-8 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ('')}
                title="지우기"
                className="absolute right-2 top-1/2 -translate-y-1/2 px-1 text-slate-400 hover:text-slate-600"
              >
                ×
              </button>
            )}
          </div>
        </div>
        <div className="w-36">
          <label className="block text-xs font-medium text-slate-500 mb-1">문서 종류</label>
          <select value={kind} onChange={(e) => setKind(e.target.value as DocKind | '')} className={selectCls}>
            <option value="">{opt('전체', items.length)}</option>
            {DOC_KINDS.map((k) => (
              <option key={k} value={k}>{opt(k, items.filter((d) => d.kind === k).length)}</option>
            ))}
          </select>
        </div>
        <div className="w-32">
          <label className="block text-xs font-medium text-slate-500 mb-1">연도</label>
          <select value={year} onChange={(e) => setYear(e.target.value as YearBucket | '')} className={selectCls}>
            <option value="">전체</option>
            {YEAR_BUCKETS.map((y) => (
              <option key={y} value={y}>{opt(y, items.filter((d) => yearBucket(d.docMonth) === y).length)}</option>
            ))}
          </select>
        </div>
        <div className="w-32">
          <label className="block text-xs font-medium text-slate-500 mb-1">결과</label>
          <select value={outcome} onChange={(e) => setOutcome(e.target.value as Outcome | '')} className={selectCls}>
            <option value="">전체</option>
            {OUTCOMES.map((o) => (
              <option key={o} value={o}>{opt(o, items.filter((d) => d.outcome === o).length)}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">
          총 <span className="font-semibold text-slate-900">{shown.length.toLocaleString()}</span>건
          {shown.length !== items.length && <span className="text-slate-400"> / 전체 {items.length}건</span>}
          <span className="text-slate-400"> · 최신순</span>
        </p>
        <p className="text-xs text-slate-400">
          실주 건도 남깁니다 — 제안서와 견적은 다음 제안의 재료입니다.
        </p>
      </div>

      <ErrorBanner message={loadError} onRetry={load} className="mb-3" />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-indigo-500 mr-2" />
          <span className="text-slate-500">불러오는 중...</span>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {shown.length === 0 ? (
            <div className="text-center py-20 text-slate-400 text-sm">
              {loadError ? '불러오지 못했습니다.' : '조건에 맞는 문서가 없습니다.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs font-semibold text-slate-500">
                  <th className="px-4 py-3">문서명</th>
                  <th className="px-4 py-3 w-44">고객사</th>
                  <th className="px-4 py-3 w-36 text-right">금액</th>
                  <th className="px-4 py-3 w-24">날짜</th>
                  <th className="px-4 py-3 w-28">검수</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {shown.map((d) => (
                  <tr
                    key={d.id}
                    onClick={() => setPickedId(d.id)}
                    className={`cursor-pointer align-top ${picked?.id === d.id ? 'bg-indigo-50/60' : 'hover:bg-slate-50'}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium flex-shrink-0 ${KIND_STYLE[d.kind]}`}>
                          {d.kind}
                        </span>
                        <span className="font-medium text-slate-900 line-clamp-2">{d.title}</span>
                      </div>
                      {d.projectName && <div className="mt-1 text-xs text-slate-400">{d.projectName}</div>}
                      {/* 태그 — 누르면 그 말로 검색한다 */}
                      <div className="mt-1.5 flex flex-wrap items-center gap-1">
                        {d.keywords.map((k) => (
                          <button
                            key={k}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setQ(k); }}
                            title={`「${k}」로 찾기`}
                            className={`rounded px-1.5 py-0.5 text-xs ${
                              q.trim() === k ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500 hover:bg-indigo-50 hover:text-indigo-600'
                            }`}
                          >
                            {k}
                          </button>
                        ))}
                        {d.outcome === '실주' && (
                          <span className="rounded px-1.5 py-0.5 text-xs font-semibold bg-red-50 text-red-600">실주</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{d.clientName || '—'}</td>
                    <td className="px-4 py-3 text-right font-medium text-slate-900 whitespace-nowrap">{won(d.amount)}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 tabular-nums whitespace-nowrap">
                      {d.docMonth.replace('-', '.')}
                      {months(d) && <div className="text-slate-400">{months(d)}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {d.reviewed ? (
                          <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-emerald-50 text-emerald-700">확인 완료</span>
                        ) : (
                          <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-amber-50 text-amber-700">검수 필요</span>
                        )}
                        {/* 추출 때 단 표시 — 계약서 안의 모순이라 검수 때 먼저 본다 */}
                        {d.flags?.map((f) => (
                          <span key={f} className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-red-50 text-red-600">{f}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      {/* 메타데이터 — 공고 상세와 같은 오른쪽 패널 */}
      {picked && (
        <SidePanel
          key={picked.id}
          onClose={() => setPickedId(null)}
          header={
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1.5 text-xs mb-1.5">
                <span className={`rounded-full px-2 py-0.5 font-medium ${KIND_STYLE[picked.kind]}`}>{picked.kind}</span>
                {picked.reviewed ? (
                  <span className="rounded px-1.5 py-0.5 font-medium bg-emerald-50 text-emerald-700">확인 완료</span>
                ) : (
                  <span className="rounded px-1.5 py-0.5 font-medium bg-amber-50 text-amber-700">검수 필요</span>
                )}
                {picked.flags?.map((f) => (
                  <span key={f} className="rounded px-1.5 py-0.5 font-medium bg-red-50 text-red-600">{f}</span>
                ))}
              </div>
              <h2 className="text-base font-bold text-slate-900 leading-snug">{picked.title}</h2>
              <p className="mt-1 text-xs text-slate-400">AI 가 뽑고 사람이 고칩니다 · p.N 을 누르면 원본 그 쪽이 열립니다</p>
            </div>
          }
          footer={
            <>
              <a
                href={picked.driveUrl} target="_blank" rel="noreferrer"
                title="공유 드라이브 구성원 계정으로 로그인돼 있어야 열린다"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-indigo-600 hover:bg-indigo-50"
              >
                Drive 에서 열기 <ExternalLink size={14} />
              </a>
              {internal && (
                <button
                  onClick={() => downloadOriginal(picked.id, picked.fileName ?? picked.title)}
                  disabled={downloading}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} 원본 받기
                </button>
              )}
              {picked.reviewed ? (
                <button
                  onClick={() => setReviewed(picked.id, false)}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                >
                  <CheckCircle2 size={14} className="text-emerald-600" /> 확인 완료 · 되돌리기
                </button>
              ) : (
                <button
                  onClick={() => setReviewed(picked.id, true)}
                  disabled={saving}
                  className="px-5 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
                >
                  확인 완료로 표시
                </button>
              )}
            </>
          }
        >
          <div className="space-y-3 text-sm">
            <Row label="프로젝트명" value={picked.projectName} />
            {picked.contract ? (
              <ArchiveContractSection doc={picked} onSaved={load} canOpen={internal} />
            ) : (
              <>
                <Row label="고객사" value={picked.clientName} />
                <Row label="기간" value={period(picked)} />
                <Row label="금액" value={won(picked.amount)} />
                {/* 계약서가 아닌 문서도 사람이 고친다 — 전엔 고칠 칸이 없었다(#111) */}
                <DocEdit doc={picked} onSaved={load} />
              </>
            )}
            <Row
              label="투입"
              value={picked.manpower
                ? `${picked.manpower.headcount}명 · ${picked.manpower.months.toFixed(1)} 인월`
                : '—'}
            />
            <Row label="사업유형" value={picked.projectType ?? '—'} />
            <div>
              <div className="text-xs font-semibold text-slate-400 mb-1">태그</div>
              <div className="flex flex-wrap gap-1">
                {picked.keywords.length === 0 ? (
                  <span className="text-slate-400">—</span>
                ) : picked.keywords.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => { setQ(k); setPickedId(null); }}
                    title={`「${k}」로 찾기`}
                    className="rounded px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-600"
                  >
                    {k}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-400 mb-1">요약</div>
              <p className="text-slate-700 leading-relaxed">{picked.summary}</p>
            </div>
            {downloadError && <p className="text-xs text-red-600">{downloadError}</p>}
          </div>
        </SidePanel>
      )}

    </div>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex gap-2">
    <span className="w-20 flex-shrink-0 text-slate-500">{label}</span>
    <span className="flex-1 font-medium text-slate-900 break-words">{value}</span>
  </div>
);

/** 계약서가 아닌 문서 — 제목 · 태그 · 문서 날짜를 고친다. 저장하면 검수로 표시된다 */
const DocEdit: React.FC<{ doc: ArchiveDoc; onSaved: () => Promise<void> | void }> = ({ doc, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: doc.title, keywords: doc.keywords.join(', '), docDate: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setOpen(false); setForm({ title: doc.title, keywords: doc.keywords.join(', '), docDate: '' }); setErr(null); }, [doc.id, doc.title, doc.keywords]);
  if (!open) {
    return <button onClick={() => setOpen(true)} className="text-xs text-indigo-600 hover:underline">제목 · 태그 · 날짜 고치기</button>;
  }
  const input = 'w-full px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500';
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
      <label className="block text-xs text-slate-500">제목<input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={input} /></label>
      <label className="block text-xs text-slate-500">태그 (쉼표로)<input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} className={input} /></label>
      <label className="block text-xs text-slate-500">문서 날짜 (비우면 그대로)<input type="date" value={form.docDate} onChange={(e) => setForm({ ...form, docDate: e.target.value })} className={input} /></label>
      {err && <p className="text-xs text-red-600">{err}</p>}
      <div className="flex gap-2">
        <button
          disabled={busy}
          onClick={async () => {
            setBusy(true); setErr(null);
            try {
              await archiveApi.updateDocument(doc.id, {
                title: form.title,
                keywords: form.keywords.split(',').map((k) => k.trim()).filter(Boolean),
                ...(form.docDate ? { docDate: form.docDate } : {}),
              });
              await onSaved();
              setOpen(false);
            } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
          }}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
        >저장 · 검수 표시</button>
        <button onClick={() => setOpen(false)} className="px-3 py-1.5 rounded-lg text-xs text-slate-600 hover:bg-slate-100">취소</button>
      </div>
    </div>
  );
};
